import React, { useMemo } from 'react';
import { getRUM } from '@codametrix/client-observability';
import { Button, Typography, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ConfigurableList, { ListSection } from '../../atoms/ConfigurableList';
import { ListItem } from '../../atoms/ConfigurableList/ListItem';
import { ViewType } from '../../organisms/workItemsTable';
import {
    NavViewSetConfig,
    DEFAULT_NAV_VIEW_SET_CONFIG,
    LEGACY_MY_WORK_VIEW_NAMES,
    LEGACY_MY_TEAM_WORK_VIEW_NAMES,
    isQueueView,
} from '../../organisms/workItemsTable/constants';
import theme from '../../../theme/Theme';
import { NAVIGATION_COLORS } from '../NavigationColumn/constants';

interface NavListItemProps {
    itemKey: string;
    title: string;
    isSelected: boolean;
    onClick: () => void;
}

const NavListItem: React.FC<NavListItemProps> = ({ itemKey, title, isSelected, onClick }) => (
    <Box key={itemKey} sx={{ position: 'relative' }}>
        <ListItem
            title={title}
            onClick={onClick}
            selected={isSelected}
            textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
            selectedTextColor={NAVIGATION_COLORS.TEXT_SELECTED}
            selectedBackgroundColor={NAVIGATION_COLORS.SELECTED_BG}
            hoverBackgroundColor={NAVIGATION_COLORS.HOVER_BG}
            sx={{
                padding: '12px 16px 12px 12px',
                borderRadius: '3px',
                margin: '0 12px 4px 12px',
                '& .MuiListItemText-primary': {
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '14px',
                    lineHeight: '16px',
                },
            }}
        />
        {isSelected && (
            <ChevronRightIcon
                sx={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 18,
                    color: NAVIGATION_COLORS.TEXT_LOW,
                    pointerEvents: 'none',
                }}
            />
        )}
    </Box>
);

interface WorklistNavigationContentProps {
    searchTerm: string;
    views: ViewType[];
    activeView?: ViewType;
    onViewChange: (view: ViewType) => void;
    roles: string[];
    onManageViewsClick?: (event: React.MouseEvent<HTMLElement>) => void;
    navViewSetConfig?: NavViewSetConfig[];
    activeNavViewSetId?: string | null;
    onNavViewSetSelect?: (viewSetId: string) => void;
}

const WorklistNavigationContent: React.FC<WorklistNavigationContentProps> = ({
    searchTerm,
    views,
    activeView,
    onViewChange,
    roles,
    onManageViewsClick,
    navViewSetConfig = DEFAULT_NAV_VIEW_SET_CONFIG.navViewConfig,
    activeNavViewSetId = null,
    onNavViewSetSelect,
}) => {
    // Check if user has coding-related roles
    const isCodingManager = roles?.includes('coding_manager');
    const hasCodingRole = roles?.some(
        (role) =>
            role === 'coding_manager' ||
            role === 'coder' ||
            role === 'cqa_coder' ||
            role === 'cqa_manager'
    );

    const categorizeViews = useMemo(() => {
        const favoritesViews: ViewType[] = [];
        const queueViews: ViewType[] = [];
        const savedViews: ViewType[] = [];

        const allLegacyNames = [...LEGACY_MY_WORK_VIEW_NAMES, ...LEGACY_MY_TEAM_WORK_VIEW_NAMES];

        const filteredViews = searchTerm
            ? views.filter(v =>
                v.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : views;

        filteredViews.forEach((view) => {
            if (view.userPreferences?.isHidden) return;
            // Hide legacy individual views that are replaced by nav view sets
            if (allLegacyNames.includes(view.name)) return;

            if (isQueueView(view)) {
                queueViews.push(view);
                return;
            }

            savedViews.push(view);
        });

        return { favoritesViews, queueViews, savedViews };
    }, [views, searchTerm]);

    const { favoritesViews, queueViews, savedViews } = categorizeViews;
   
    const createViewListItem = (view: ViewType) => (
        <NavListItem
            itemKey={view.viewId}
            title={view.name}
            isSelected={activeView?.viewId === view.viewId}
            onClick={() => {
                getRUM().addAction('~worklist_side_nav_view_clicked', {
                    viewName: view.name,
                });
                onViewChange(view);
            }}
        />
    );

    const sections: ListSection[] = [];

    // FAVORITES Section - Always show (even if empty for now)
    if (favoritesViews.length > 0) {
        sections.push({
            title: "FAVORITES",
            rows: favoritesViews.map(createViewListItem),
            headerStyle: {
                padding: sections.length === 0 ? '8px 16px 4px 16px' : '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px'
            }
        });
    }

    const createNavViewSetListItem = (vs: NavViewSetConfig) => (
        <NavListItem
            itemKey={vs.id}
            title={vs.label}
            isSelected={activeNavViewSetId === vs.id}
            onClick={() => onNavViewSetSelect?.(vs.id)}
        />
    );

    // MY WORK Section — one item per view set with section === 'MY_WORK'
    const myWorkViewSets = navViewSetConfig.filter((vs) => vs.section === 'MY_WORK');
    if (hasCodingRole && myWorkViewSets.length > 0) {
        sections.push({
            // title: "MY WORK",
            rows: myWorkViewSets.map(createNavViewSetListItem),
            headerStyle: {
                padding: sections.length === 0 ? '8px 16px 4px 16px' : '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px',
            },
        });
    }

    // MY TEAM'S WORK Section — one item per view set with section === 'MY_TEAM_WORK'
    const myTeamViewSets = navViewSetConfig.filter((vs) => vs.section === 'MY_TEAM_WORK');
    if (isCodingManager && myTeamViewSets.length > 0) {
        sections.push({
            rows: myTeamViewSets.map(createNavViewSetListItem),
            headerStyle: {
                padding: '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px',
            },
        });
    }

    // QUEUES Section
    if (queueViews.length > 0) {
        sections.push({
            title: "QUEUES",
            rows: queueViews.map(createViewListItem),
            headerStyle: {
                padding: '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px',
            },
        });
    }

    // SAVED VIEWS Section
    if (savedViews.length > 0) {
        const isFirstSection = sections.length === 0;
        sections.push({
            component: () => (
                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isFirstSection ? '8px 16px 4px 16px' : '16px 16px 4px 16px'
                    }}
                >
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.artisticTextLow.main,
                            fontWeight: 500,
                            fontSize: '9.72px',
                            letterSpacing: '0.5px',
                            lineHeight: '12px',
                            textTransform: 'uppercase'
                        }}
                    >
                        SAVED VIEWS
                    </Typography>
                    {onManageViewsClick && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                                getRUM().addAction('~worklist_side_nav_manage_view_clicked', {});
                                onManageViewsClick(e);
                            }}
                            sx={{
                                color: NAVIGATION_COLORS.TEXT_SELECTED,
                                borderColor: NAVIGATION_COLORS.TEXT_SELECTED,
                                textTransform: 'none',
                                fontSize: '11px',
                                padding: '2px 8px',
                                minHeight: 'unset',
                                '&:hover': {
                                    borderColor: NAVIGATION_COLORS.TEXT_SELECTED,
                                    backgroundColor: 'rgba(229, 146, 125, 0.08)',
                                },
                            }}
                        >
                            Manage Views
                        </Button>
                    )}
                </Box>
            ),
            rows: savedViews.map(createViewListItem)
        });
    }

    return (
        <ConfigurableList
            sections={sections}
            containerStyle={{
                backgroundColor: 'transparent',
                margin: 0,
                padding: 0,
            }}
            textColor={theme.palette.artisticTextLow.main}
        />
    );
};

export default WorklistNavigationContent;
